import React, { useContext, useState } from 'react'
import { useHistory, useParams, useRouteMatch } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { flushSync } from 'react-dom'
import { MythologicalMessageSummary } from './messageSummaries/MythologicalMessageSummary'
import { HolocaustMessageSummary } from './messageSummaries/HolocaustMessageSummary'
import { HolocaustReportMessageSummary } from './messageSummaries/HolocaustReportMessageSummary'
import { FamousMessageSummary } from './messageSummaries/FamousMessageSummary'
import { ReligiousMessageSummary } from './messageSummaries/ReligiousMessageSummary'
import { HistoricalMessageSummary } from './messageSummaries/HistoricalMessageSummary'
import { OneHundredTenYearsMessageSummary } from './messageSummaries/OneHundredTenYearsMessageSummary'
import InappropriateMessageSummary from './messageSummaries/InappropriateMessageSummary'
import { TransitionMessageSummary } from './messageSummaries/TransitionMessageSummary'
import { HybridSensitiveMessageSummary } from './messageSummaries/HybridSensitiveMessageSummary'
import { SensitiveMessageSummary } from './messageSummaries/SensitiveMessageSummary'
import { SexChangedMessageSummary } from './messageSummaries/SexChangedMessageSummary'
import { deepCopy } from '../../../util/Utils'
import useNotifications from '../../../hooks/useNotifications'
import { useQueryParams } from '../../../util/UrlUtil'
import { QueuesContext } from '../QueuesContext'
import { ReservationHeldByNonContributorMessageSummary } from './messageSummaries/ReservationHeldByNonContributorMessageSummary'
import { FictitiousMessageSummary } from './messageSummaries/FictitiousMessageSummary'
import { SlaveryAssociationSummary } from './messageSummaries/SlaveryAssociationSummary'
import { TempleReturnsMessageSummary } from './messageSummaries/TempleReturnsMessageSummary'
import SameSexDetectedMessageSummary from './messageSummaries/SameSexDetectedMessageSummary'
import { BirthDateChangeReservationsMessageSummary } from './messageSummaries/BirthDateChangeReservationsMessageSummary'
import CmisxMismatchMessageSummary from './messageSummaries/CmisxMismatchMessageSummary'
import { useQueueCaches } from './hooks/useQueueCaches'
import { deleteMessagesBulk, getQueueByName, getQueueMessages } from '../../../services/dmqueuesService'
import { useQueueButtons } from './hooks/useQueueButtons'
import { SimpleMessageSummary } from './messageSummaries/SimpleMessageSummary'
import { MemoriesAddedMessageSummary } from './messageSummaries/MemoriesAddedMessageSummary'

export default () => {}

/* eslint-disable react/destructuring-assignment */

export const queueNames = {
  BIRTH_DATE_CHANGE_WITH_RESERVATIONS_QUEUES: 'BIRTH_DATE_CHANGE_WITH_RESERVATIONS',
  CMISX_MISMATCH: 'CMISX_MISMATCH',
  DEITY_QUEUES: 'DEITY',
  FAMOUS_PERSON_QUEUES: 'FAMOUS_PERSON',
  FOCUSED_USER_QUEUES: 'FOCUSED_USER',
  FICTITIOUS: 'FICTITIOUS',
  HISTORICAL_PERSON_QUEUES: 'HISTORICAL_PERSON',
  HOLOCAUST_ORDINANCE_REPORT_QUEUES: 'HOLOCAUST_ORDINANCE_REPORT',
  HOLOCAUST_QUEUES: 'HOLOCAUST',
  HYBRID_SENSITIVE_TRANSITION_QUEUES: 'HYBRID_SENSITIVE_TRANSITION',
  HYBRID_SENSITIVE_QUEUES: 'HYBRID_SENSITIVE',
  MYTHOLOGICAL: 'MYTHOLOGICAL',
  ONE_HUNDRED_TEN_YEARS_QUEUES: 'ONE_HUNDRED_TEN_YEAR',
  PROFANITY_QUEUES: 'PROFANITY',
  RELIGIOUS_TITLE_QUEUES: 'RELIGIOUS_TITLE',
  RESERVATION_HELD_BY_NON_CONTRIBUTORS: 'RESERVATION_HELD_BY_NON_CONTRIBUTORS',
  SENSITIVE_REEVALUATION_QUEUES: 'SENSITIVE_REEVALUATION',
  SENSITIVE_QUEUES: 'SENSITIVE',
  SEX_CHANGED_QUEUES: 'SEX_CHANGED',
  SLAVERY_ASSOCIATION: 'SLAVERY_ASSOCIATION',
  SAME_SEX_DETECTED: 'SAME_SEX_DETECTED',
  TEMPLE_RETURNS_QUEUES: 'TEMPLE_RETURNS',
  MEMORIES_ADDED: 'MEMORIES_ADDED',
}

const getQueueKeys = (queueName) => Object.values(queueNames).find((standardName) => queueName.includes(standardName))

export const treeCategoryMap = {
  familyTree: 'FAMILY_TREE',
  sharedFamilyGroup: 'SHARED_FAMILY_GROUP',
  cet: 'CET',
  secondary: 'SECONDARY',
}

export function useMessagesListController({ compactMessageView = false } = {}) {
  const { t } = useTranslation()
  const { queueButtons, queueButtonsDialogs } = useQueueButtons()
  const { queueCachesState, queueCachesActions } = useQueueCaches()
  const notifications = useNotifications()
  const queryParams = useQueryParams()
  const pathParams = useParams()
  const history = useHistory()
  const match = useRouteMatch()

  const { openTabs } = useContext(QueuesContext)

  const queueStorageKey = Object.keys(localStorage).find((key) => key.includes('DMQueuePaginator'))
  let pageLimit = 10
  if (queueStorageKey && compactMessageView) {
    pageLimit = localStorage.getItem(queueStorageKey)
  }

  const initialState = {
    selectedCheckboxes: {},
    checkboxConfirmLimit: 10,
    confirmDialogOpen: false,
    queueName: null,
    queueType: null,
    queueTotal: 100,
    queuePageIndex: null,
    pageLimit: Number(pageLimit),
    topButtons: [],
    tableButtons: [],
    sortButtons: [],
    sortDirection: null,
    sortField: null,
    treeCategories: queryParams.get('treeCategories')?.split(',') || [],
    permissions: [],
    summaryComponent: () => {},
    queueContents: [],
    viewButtonCallback: () => {},
    query: '',
  }

  const [queueState, setQueueState] = useState(initialState)

  const queueActions = {
    isCheckboxSelected: (message) => {
      return !!queueState.selectedCheckboxes[message.id]
    },
    addCheckboxSelection: (message, checked) => {
      if (checked) {
        setQueueState((prevState) => ({
          ...prevState,
          selectedCheckboxes: { ...prevState.selectedCheckboxes, [message.id]: deepCopy(message) },
        }))
      } else {
        setQueueState((prevState) => {
          const newCheckboxes = { ...prevState.selectedCheckboxes }
          delete newCheckboxes[message.id]
          return {
            ...prevState,
            selectedCheckboxes: newCheckboxes,
          }
        })
      }
    },
    selectAllCheckboxes: (event) => {
      const isChecked = event.target.checked

      if (isChecked) {
        setQueueState((prevState) => {
          const updatedCheckboxes = { ...prevState.selectedCheckboxes }
          prevState.queueContents.forEach((message) => {
            updatedCheckboxes[message.id] = deepCopy(message)
          })
          return {
            ...prevState,
            selectedCheckboxes: updatedCheckboxes,
          }
        })
      } else {
        setQueueState((prevState) => ({
          ...prevState,
          selectedCheckboxes: {},
        }))
      }
    },
    clearCheckboxes: () => {
      setQueueState((prevState) => ({
        ...prevState,
        selectedCheckboxes: {},
      }))
    },
    checkFinalPageAndReload: (isTopButton) => {
      const firstEntryIndex = queueState.queuePageIndex * queueState.pageLimit
      if (
        queueState.queuePageIndex > 0 &&
        queueState.queueTotal - (isTopButton ? Object.keys(queueState.selectedCheckboxes).length : 1) <= firstEntryIndex
      ) {
        const newPageIndex = Math.max(queueState.queuePageIndex - 1, 0)
        setQueueState((prevState) => ({
          ...prevState,
          queuePageIndex: newPageIndex,
        }))
        history.replace(queueActions.getPaginationUrl(newPageIndex))
      } else {
        queueActions.loadPagination()
      }
    },
    pressTopButton: (buttonCallback) => {
      const promises = []
      Object.keys(queueState.selectedCheckboxes).forEach((messageId) => {
        const message = queueState.selectedCheckboxes[messageId]
        promises.push(buttonCallback({ message }))
      })
      Promise.allSettled(promises)
        .then((responses) => {
          let failedMessages = ''
          responses.forEach((result) => {
            if (result.status === 'rejected') {
              failedMessages += `${result.reason}.`
            }
          })
          if (failedMessages.length > 0) {
            notifications.error(failedMessages)
          } else {
            notifications.info(
              t('queues.info.executedActionToMessages', {
                defaultvalue: `Executed action to ${promises.length} messages`,
                count: promises.length,
              })
            )
          }
          const ids = responses.map((r) => r.value?.messageId).filter(Boolean)
          const resolution = responses.find((r) => r.value?.resolution)?.value?.resolution
          deleteMessagesBulk(ids, resolution)
            .then(() => {
              queueActions.checkFinalPageAndReload(true)
            })
            .catch((err) => {
              notifications.error(
                t('queues.errors.bulkDelete', 'Failed to bulk delete messages: {error}', { error: JSON.stringify(err) })
              )
            })
        })
        .catch((err) => {
          notifications.error(
            t('queues.errors.Checkboxes', {
              defaultvalue: `Logging error in checkboxes ${JSON.stringify(err)}`,
              error: JSON.stringify(err),
            })
          )
        })
    },
    prepareTemplate: async () => {
      const queueName = pathParams.queueName
      const queuePageIndex = Number(queryParams.get('queuePageIndex')) || 0
      let queueType = null
      try {
        const queue = await getQueueByName(queueName, true)
        queueType = queue.data.description
      } catch (err) {
        notifications.error(
          t('queues.errors.getQueueByName', `Error getting queue by name: {message}`, {
            message: err?.message,
          }),
          err
        )
      }

      const tableButtons = []
      const topButtons = []
      let viewButtonCallback
      let summaryComponent

      switch (getQueueKeys(queueName)) {
        case queueNames.HOLOCAUST_ORDINANCE_REPORT_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <HolocaustReportMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.HOLOCAUST_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.yadVashemViewButtonAction
          summaryComponent = (message) => (
            <HolocaustMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.FAMOUS_PERSON_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('queues.notFamous', 'Not Famous'), queueButtons.queueButtonNotFamous)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('famous', 'Famous'), null, (message, onSubmitCallback) =>
              queueButtonsDialogs.famousDialog(
                message,
                message.details.personId,
                message.details.name,
                onSubmitCallback
              )
            )
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.notFamous', 'Not Famous'), queueButtons.queueButtonNotFamous)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <FamousMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.RESERVATION_HELD_BY_NON_CONTRIBUTORS:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <ReservationHeldByNonContributorMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.RELIGIOUS_TITLE_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.restrict', 'Restrict'), queueButtons.queueButtonReligiousTitle)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('queues.unrestrict', 'Unrestrict'), queueButtons.queueButtonNotReligiousTitle)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.restrict', 'Restrict'), queueButtons.queueButtonReligiousTitle)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.unrestrict', 'Unrestrict'), queueButtons.queueButtonNotReligiousTitle)
          )
          tableButtons.push(queueActions.buttonTemplate(t('queues.google', 'Google'), queueButtons.queueButtonGoogle))
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <ReligiousMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.HISTORICAL_PERSON_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <HistoricalMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.ONE_HUNDRED_TEN_YEARS_QUEUES:
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('personId', 'Person ID'), 'messageEntry.personId')
          )
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('contributorId', 'Contributor ID'), 'messageEntry.contributorId')
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <OneHundredTenYearsMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.PROFANITY_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.profanity', 'Profanity'), queueButtons.queueButtonProfanity)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.notProfanity', 'Not Profanity'), queueButtons.queueButtonNotProfanity)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.profanity', 'Profanity'), queueButtons.queueButtonProfanity)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.notProfanity', 'Not Profanity'), queueButtons.queueButtonNotProfanity)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <InappropriateMessageSummary
              messageDetails={message.details}
              term="unwantedTerm"
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.DEITY_QUEUES:
          topButtons.push(queueActions.buttonTemplate(t('action.sacred', 'Sacred'), queueButtons.queueButtonSacred))
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(queueActions.buttonTemplate(t('action.sacred', 'Sacred'), queueButtons.queueButtonSacred))
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <InappropriateMessageSummary
              messageDetails={message.details}
              term="deityTerm"
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.BIRTH_DATE_CHANGE_WITH_RESERVATIONS_QUEUES:
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <BirthDateChangeReservationsMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.HYBRID_SENSITIVE_TRANSITION_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.transition', 'Transition'), queueButtons.queueButtonTransition)
          )
          tableButtons.push(
            queueActions.buttonTemplate(
              t('queues.notTransition', 'Not Transition'),
              queueButtons.queueButtonNotTransition
            )
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <TransitionMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.HYBRID_SENSITIVE_QUEUES:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('queues.hybridPublic', 'Hybrid Public'), queueButtons.queueButtonHybridPublic)
          )
          topButtons.push(
            queueActions.buttonTemplate(
              t('queues.hybridPrivate', 'Hybrid Private'),
              queueButtons.queueButtonHybridPrivate
            )
          )
          topButtons.push(
            queueActions.buttonTemplate(t('queues.notHybrid', 'Not Hybrid'), queueButtons.queueButtonNotHybrid)
          )
          topButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))

          tableButtons.push(
            queueActions.buttonTemplate(t('queues.hybridPublic', 'Hybrid Public'), queueButtons.queueButtonHybridPublic)
          )
          tableButtons.push(
            queueActions.buttonTemplate(
              t('queues.hybridPrivate', 'Hybrid Private'),
              queueButtons.queueButtonHybridPrivate
            )
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.notHybrid', 'Not Hybrid'), queueButtons.queueButtonNotHybrid)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears)
          )
          tableButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <HybridSensitiveMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.SENSITIVE_REEVALUATION_QUEUES:
          topButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))
          topButtons.push(
            queueActions.buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive)
          )
          topButtons.push(
            queueActions.buttonTemplate(
              t('queues.hybridPrivate', 'Hybrid Private'),
              queueButtons.queueButtonHybridPrivate
            )
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive)
          )
          tableButtons.push(
            queueActions.buttonTemplate(
              t('queues.hybridPrivate', 'Hybrid Private'),
              queueButtons.queueButtonHybridPrivate
            )
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <SensitiveMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.SENSITIVE_QUEUES:
          topButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))
          topButtons.push(
            queueActions.buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(queueActions.buttonTemplate(t('sensitive', 'Sensitive'), queueButtons.queueButtonSensitive))
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.notSensitive', 'Not Sensitive'), queueButtons.queueButtonNotSensitive)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('queues.forebears', 'Forebears'), queueButtons.queueButtonForebears)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <SensitiveMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.TEMPLE_RETURNS_QUEUES:
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('personId', 'Person ID'), 'messageEntry.personId')
          )
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('contributorId', 'Contributor ID'), 'messageEntry.contributorId')
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true),
            queueActions.buttonTemplate(t('action.recycle', 'Recycle'), queueButtons.queueButtonRecycle),
            queueActions.buttonTemplate(t('action.unreserve', 'Unreserve'), queueButtons.queueButtonUnreserve)
          )
          viewButtonCallback = null
          summaryComponent = (message) => (
            <TempleReturnsMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.SEX_CHANGED_QUEUES:
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('personId', 'Person ID'), 'messageEntry.personId')
          )
          queueState.sortButtons.push(
            queueActions.sortButtonTemplate(t('contributorId', 'Contributor ID'), 'messageEntry.contributorId')
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = null
          summaryComponent = (message) => (
            <SexChangedMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.MYTHOLOGICAL:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('mythological', 'Mythological'), queueButtons.queueButtonMythological)
          )
          topButtons.push(
            queueActions.buttonTemplate(
              t('queues.notMythological', 'Not Mythological'),
              queueButtons.queueButtonNotMythological
            )
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('mythological', 'Mythological'), queueButtons.queueButtonMythological)
          )
          tableButtons.push(
            queueActions.buttonTemplate(
              t('queues.notMythological', 'Not Mythological'),
              queueButtons.queueButtonNotMythological
            )
          )
          tableButtons.push(queueActions.buttonTemplate(t('queues.google', 'Google'), queueButtons.queueButtonGoogle))
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <MythologicalMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.FICTITIOUS:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          topButtons.push(
            queueActions.buttonTemplate(t('fictitious', 'Fictitious'), queueButtons.queueButtonFictitious)
          )
          topButtons.push(
            queueActions.buttonTemplate(
              t('queues.notFictitious', 'Not Fictitious'),
              queueButtons.queueButtonNotFictitious
            )
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('fictitious', 'Fictitious'), queueButtons.queueButtonFictitious)
          )
          tableButtons.push(
            queueActions.buttonTemplate(
              t('queues.notFictitious', 'Not Fictitious'),
              queueButtons.queueButtonNotFictitious
            )
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <FictitiousMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.SLAVERY_ASSOCIATION:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <SlaveryAssociationSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.SAME_SEX_DETECTED:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          summaryComponent = (message) => (
            <SameSexDetectedMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.CMISX_MISMATCH:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          summaryComponent = (message) => (
            <CmisxMismatchMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        case queueNames.MEMORIES_ADDED:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <MemoriesAddedMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
          break
        default:
          topButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          tableButtons.push(
            queueActions.buttonTemplate(t('action.delete', 'Delete'), queueButtons.deleteMessage, undefined, true)
          )
          viewButtonCallback = queueActions.viewButtonAction
          summaryComponent = (message) => (
            <SimpleMessageSummary
              messageDetails={message.details}
              queueCachesState={queueCachesState}
              queueCachesActions={queueCachesActions}
            />
          )
      }
      setQueueState((prevState) => {
        return {
          ...prevState,
          topButtons,
          tableButtons,
          viewButtonCallback,
          summaryComponent,
          queueName,
          queueType,
          queuePageIndex,
        }
      })
    },
    setQueuePageIndex: (queuePageIndex) => {
      flushSync(() => {
        setQueueState((prevState) => ({
          ...prevState,
          queuePageIndex: Number(queuePageIndex),
        }))
      })

      history.replace(queueActions.getPaginationUrl(queuePageIndex))
    },
    setSelectedMessage: (selectedMessage) => {
      setQueueState((prevState) => ({
        ...prevState,
        selectedMessage,
      }))
    },
    loadPagination: async () => {
      if (!queueState.queueName || !queueState.pageLimit) {
        return Promise.resolve()
      }
      queueActions.clearCheckboxes()

      try {
        const treeCategories = [...queueState.treeCategories]
        queueState.treeCategories.includes('FAMILY_TREE') && treeCategories.push('SHARED_FAMILY_GROUP')
        const response = await getQueueMessages({
          queueName: queueState.queueName,
          params: {
            pageNum: queueState.queuePageIndex,
            pageSize: queueState.pageLimit,
            query: queueState.query,
            sortDirection: queueState.sortDirection,
            sortField: queueState.sortField,
            treeCategories,
          },
        })
        const contributorIds = new Set(
          response.data.messages.map((message) => message?.details?.contributorId).filter(Boolean)
        )
        queueCachesActions.fetchUserSummaries(contributorIds)
        setQueueState((prevState) => ({
          ...prevState,
          queueContents: response.data.messages.map((message) => {
            const parsedMessage = message.details.message && JSON.parse(message.details.message)
            return { ...message, details: { ...message.details, ...parsedMessage } }
          }),
          queueTotal: response.data.totalCount,
        }))
        return Promise.resolve()
      } catch (err) {
        notifications.error(
          t('queues.errors.gettingMessagesList', {
            defaultValue: t('queues.errors.gettingMessagesList', {
              defaultValue: `Error getting messages list. Error: ${err?.message}`,
              message: err?.message,
            }),
            message: err?.message,
          }),
          err
        )
        return Promise.resolve() // the error has been handled so continue
      }
    },
    setPageLimit: (newLimit) => {
      if (newLimit !== queueState.pageLimit) {
        flushSync(() => {
          const firstEntryIndex = queueState.queuePageIndex * queueState.pageLimit
          const newPageIndex = Math.floor(firstEntryIndex / Number(newLimit))
          setQueueState((prevState) => ({
            ...prevState,
            queuePageIndex: newPageIndex,
          }))
          history.replace(queueActions.getPaginationUrl(newPageIndex))
          setQueueState((prevState) => ({
            ...prevState,
            pageLimit: Number(newLimit),
          }))
        })
      }
    },
    backToQueue: () => {
      queueActions.setSelectedMessage(null)
      queueActions.loadPagination()
    },
    buttonTemplate,
    sortButtonTemplate: (buttonText, predicate) => {
      return {
        buttonText,
        predicate,
      }
    },
    getPaginationUrl: (newPageIndex) => {
      return `${match.url}?${navigationQueryParams({ queuePageIndex: newPageIndex, treeCategories: queueState.treeCategories })}`
    },
    getMessageUrl: (message) => {
      return `${match.url}/${message.id}?${navigationQueryParams({ queuePageIndex: queueState.queuePageIndex, treeCategories: queueState.treeCategories })}`
    },
    viewButtonAction: (message, url) => {
      history.push(url)
    },
    yadVashemViewButtonAction: (message, url) => {
      openTabs(message, url)
    },
    setQueueState,
  }

  return { queueState, queueActions, queueCachesState }
}

export function buttonTemplate(buttonText, buttonCallback, dialogModalComponent, isDestructive = false) {
  return {
    buttonText,
    buttonCallback,
    dialogModalComponent,
    isDestructive,
  }
}

export function navigationQueryParams({ queuePageIndex, personId, treeCategories }) {
  const paramObj = { queuePageIndex, personId, treeCategories }
  const filtered = Object.fromEntries(
    Object.entries(paramObj).filter(([, v]) => v !== null && v !== undefined && v !== '' && v.length !== 0)
  )
  const params = new URLSearchParams(filtered)
  return params.toString()
}

/* eslint-enable react/destructuring-assignment */
